/**
 * File de revue des signalements de contenu.
 *
 * 🔴 Exigée par les deux stores : Apple (règle 1.2) et Google Play demandent un
 * signalement dans l'app ET une réponse « en temps utile ». Le mécanisme de
 * signalement existait depuis la migration 135 ; sans cet écran, les
 * signalements s'empilaient en base et personne ne les lisait — ce qui est pire
 * qu'une absence de file, parce que ça donne l'illusion d'un traitement.
 *
 * ⚠️ **Statuer n'efface AUCUN contenu.** Le retrait éventuel passe par les
 * chemins existants (archiver une liste, exclure d'une guilde), avec leurs
 * propres garde-fous. Confondre « j'ai lu » et « j'ai supprimé » ferait de cet
 * écran un bouton de suppression en un clic — et le signalement deviendrait une
 * arme entre camarades.
 *
 * ⚠️ L'instantané affiché est celui pris AU MOMENT du signalement. C'est
 * volontaire : sans lui, un auteur qui édite après coup rendrait le signalement
 * inintelligible au relecteur.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Box, Alert, CircularProgress, Paper, Button, Chip, MenuItem, Select,
  FormControl, InputLabel, Collapse, TextField,
} from '@mui/material';
import { Refresh as RefreshIcon, LockOpen as LockOpenIcon } from '@mui/icons-material';
import { AdminService } from '../../services/adminService';

const MOTIF_LABELS = {
  insultes: 'Insultes',
  harcelement: 'Harcèlement',
  inapproprie: 'Inapproprié',
  faux_exercice: 'Faux exercice',
  autre: 'Autre',
};

const STATUT_COULEURS = {
  nouveau: 'error',
  en_cours: 'warning',
  traite: 'success',
  rejete: 'default',
};

function Instantane({ snapshot }) {
  if (!snapshot) return <Typography variant="body2" color="text.secondary">— pas d’instantané —</Typography>;
  return (
    <Box sx={{ bgcolor: '#f8f8f8', p: 1.5, borderRadius: 1 }}>
      {snapshot.title && <Typography variant="subtitle2">{snapshot.title}</Typography>}
      {snapshot.name && <Typography variant="subtitle2">{snapshot.name}</Typography>}
      {snapshot.displayName && <Typography variant="subtitle2">{snapshot.displayName}</Typography>}
      {/* Signaler un joueur, c'est signaler ce qu'il a écrit : son pseudo ET le
          nom de son compagnon, les deux seuls textes libres qu'un camarade voit. */}
      {snapshot.companionNom && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          compagnon : {snapshot.companionNom}
        </Typography>
      )}
      {snapshot.description && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>{snapshot.description}</Typography>
      )}
      {Array.isArray(snapshot.words) && snapshot.words.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {snapshot.words.map((m) => <Chip key={m} label={m} size="small" />)}
        </Box>
      )}
    </Box>
  );
}

export default function ContentReportsScreen() {
  const [reports, setReports] = useState([]);
  const [statut, setStatut] = useState('nouveau');
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [note, setNote] = useState('');

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      const res = await AdminService.getContentReports(statut ? { status: statut } : {});
      setReports(res?.reports || []);
    } catch (e) {
      setErreur(e?.message || 'Impossible de charger la file');
    } finally {
      setLoading(false);
    }
  }, [statut]);

  useEffect(() => { charger(); }, [charger]);

  const statuer = async (id, nouveauStatut) => {
    try {
      await AdminService.reviewContentReport(id, nouveauStatut, note);
      setNote('');
      setOuvert(null);
      charger();
    } catch (e) {
      setErreur(e?.message || 'Impossible de mettre à jour');
    }
  };

  const rouvrirCreation = async (uid) => {
    try {
      await AdminService.liftModerationStrikes(uid);
      setErreur(null);
    } catch (e) {
      setErreur(e?.message || 'Impossible de lever les refus');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Signalements</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Statut</InputLabel>
          <Select value={statut} label="Statut" onChange={(e) => setStatut(e.target.value)}>
            <MenuItem value="nouveau">Nouveaux</MenuItem>
            <MenuItem value="en_cours">En cours</MenuItem>
            <MenuItem value="traite">Traités</MenuItem>
            <MenuItem value="rejete">Rejetés</MenuItem>
            <MenuItem value="">Tous</MenuItem>
          </Select>
        </FormControl>
        <Button startIcon={<RefreshIcon />} onClick={charger}>Rafraîchir</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Statuer ne supprime aucun contenu. Le retrait passe par l’archivage d’une liste ou
        l’exclusion d’une guilde — un signalement traité en un clic deviendrait une arme.
      </Alert>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Aucun signalement dans cette file.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Motif</TableCell>
                <TableCell>Cible</TableCell>
                <TableCell>Signalé par</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => (
                <React.Fragment key={r.id}>
                  <TableRow hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleString('fr-FR')}</TableCell>
                    <TableCell>{MOTIF_LABELS[r.reason] || r.reason}</TableCell>
                    <TableCell>{r.targetType} #{r.targetId}</TableCell>
                    <TableCell>{r.reporterName || r.reporterUid}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={STATUT_COULEURS[r.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => setOuvert(ouvert === r.id ? null : r.id)}>
                        {ouvert === r.id ? 'Fermer' : 'Examiner'}
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={ouvert === r.id} unmountOnExit>
                        <Box sx={{ py: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Contenu au moment du signalement
                          </Typography>
                          <Instantane snapshot={r.snapshot} />

                          {r.details && (
                            <Typography variant="body2" sx={{ mt: 1.5 }}>
                              <strong>Ce qu’a écrit la personne :</strong> {r.details}
                            </Typography>
                          )}

                          <TextField
                            fullWidth size="small" multiline minRows={2} sx={{ mt: 2 }}
                            label="Note de revue (facultatif)"
                            value={ouvert === r.id ? note : ''}
                            onChange={(e) => setNote(e.target.value)}
                          />

                          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                            <Button variant="outlined" onClick={() => statuer(r.id, 'en_cours')}>
                              Prendre en charge
                            </Button>
                            <Button variant="contained" color="success" onClick={() => statuer(r.id, 'traite')}>
                              Traité
                            </Button>
                            <Button variant="outlined" color="inherit" onClick={() => statuer(r.id, 'rejete')}>
                              Sans suite
                            </Button>
                            {/* Un faux positif ne doit pas enfermer : la sanction se lève. */}
                            {r.snapshot?.createdBy && (
                              <Button
                                startIcon={<LockOpenIcon />}
                                color="warning"
                                onClick={() => rouvrirCreation(r.snapshot.createdBy)}
                              >
                                Rouvrir la création pour l’auteur
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
